import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { getComicById, getPopularComics } from '@/mocks/comics';

export default function ComicPage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || '');
  const relatedComics = getPopularComics(4).filter((c) => c.id !== id);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-20 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">漫画不存在</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const pageTitle = `${comic.title}韩漫免费阅读_${comic.title}漫画在线`;
  const pageDescription = `${comic.description}作者：${comic.author}，分类：${comic.category}，状态：${comic.status}。韩国漫画网提供${comic.title}韩漫免费在线阅读。`;

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        keywords={`${comic.title},${comic.category}韩漫,韩国漫画,韩漫免费阅读,${comic.author}`}
        canonicalUrl={`/comic/${comic.id}`}
        ogType="article"
        ogImage={comic.cover}
        schemaData={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: comic.title,
            description: comic.description,
            author: { '@type': 'Person', name: comic.author },
            publisher: { '@type': 'Organization', name: '韩国漫画网', url: 'https://www.koreancomic.cn' },
            image: comic.cover,
            datePublished: comic.chapters[0]?.date || '2026-08-01',
            dateModified: comic.updatedAt,
            url: `https://www.koreancomic.cn/comic/${comic.id}`,
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, item: { '@id': 'https://www.koreancomic.cn', name: '首页' } },
                { '@type': 'ListItem', position: 2, item: { '@id': `https://www.koreancomic.cn/category/${comic.categorySlug}`, name: comic.category } },
                { '@type': 'ListItem', position: 3, item: { '@id': `https://www.koreancomic.cn/comic/${comic.id}`, name: comic.title } },
              ],
            },
          },
        ]}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-20">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: comic.category, href: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]}
          />

          <article className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <div className="rounded-xl overflow-hidden bg-background-200 aspect-[3/4] max-w-xs mx-auto lg:max-w-none">
                  <img
                    src={comic.cover}
                    alt={`${comic.title}封面`}
                    title={`${comic.title} - 韩国漫画网`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                {comic.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">
                  {comic.category}
                </span>
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                  comic.status === '连载中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
                <span className="text-sm text-foreground-400">
                  <i className="ri-star-fill text-accent-500 mr-1"></i>
                  {comic.rating}
                </span>
                <span className="text-sm text-foreground-400">
                  作者：{comic.author}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 bg-background-100 text-foreground-500 text-xs rounded-md">
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-100 border border-background-200/70 rounded-xl p-5 md:p-6 mb-8">
                <h2 className="font-heading font-semibold text-base text-foreground-900 mb-3">
                  作品简介
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">
                  {comic.longDescription}
                </p>
              </div>

              <div>
                <h2 className="font-heading font-semibold text-lg text-foreground-900 mb-4">
                  章节列表
                  <span className="text-sm text-foreground-400 ml-2 font-normal">
                    （共{comic.chapters.length}话）
                  </span>
                </h2>
                <div className="border border-background-200/70 rounded-xl overflow-hidden divide-y divide-background-200/70">
                  {comic.chapters.map((chapter) => (
                    <div
                      key={chapter.number}
                      className="flex items-center justify-between p-4 md:px-6 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-full bg-primary-50 text-primary-600 text-xs font-medium flex items-center justify-center flex-shrink-0">
                          {chapter.number}
                        </span>
                        <div>
                          <h3 className="text-sm font-medium text-foreground-800">
                            {chapter.title}
                          </h3>
                        </div>
                      </div>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">
                        {chapter.date}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </article>

          {relatedComics.length > 0 && (
            <section className="mt-16 pt-12 border-t border-background-200/70">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((rc) => (
                  <Link
                    key={rc.id}
                    to={`/comic/${rc.id}`}
                    className="group"
                  >
                    <div className="relative overflow-hidden rounded-xl mb-3 aspect-[3/4] bg-background-200">
                      <img
                        src={rc.cover}
                        alt={`${rc.title} - 韩国漫画网推荐`}
                        title={`${rc.title} - 相关推荐`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 bg-primary-500 text-white text-xs font-medium rounded-md">
                          {rc.rating}
                        </span>
                      </div>
                    </div>
                    <h3 className="font-heading font-semibold text-sm text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {rc.title}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-1">{rc.author}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}